import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";
import CurrencyDollarIcon from "@heroicons/react/24/outline/CurrencyDollarIcon";
import ClipboardDocumentListIcon from "@heroicons/react/24/outline/ClipboardDocumentListIcon";
import UserGroupIcon from "@heroicons/react/24/outline/UserGroupIcon";
import Cog6ToothIcon from "@heroicons/react/24/outline/Cog6ToothIcon";
import BanknotesIcon from "@heroicons/react/24/outline/BanknotesIcon";
import BuildingLibraryIcon from "@heroicons/react/24/outline/BuildingLibraryIcon";
import WrenchScrewdriverIcon from "@heroicons/react/24/outline/WrenchScrewdriverIcon";
import HasAccess, { csrRoles } from "../../components/HasAccess";
import HomeIcon from "./HomeIcon";
import { getTransactionsPageDefaultUrl } from "../../utils/transaction";
import { getOnboardingPageDefaultUrl } from "../../utils/accounts";
import { logPageVisit } from "../../utils/appInsightsLogger";
import { accountConfigAtom } from "../../atoms";
import { useAtom } from "jotai";
import Button from "../../components/Button";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Transition } from "@headlessui/react";
import banner from "../../assets/capitals/banner.jpeg";
import { useCookies } from "react-cookie";

function Home(): JSX.Element {
  const { data: currentUserAccount } = useCurrentUserAccount();
  const [accountConfigData] = useAtom(accountConfigAtom);
  const navigate = useNavigate();
  const [cookies, setCookies] = useCookies(["hideCapitalsBanner"]);
  const [showCapitalBanner, setShowCapitalBanner] = useState(false);

  const isPendingOrRestricted = ["Pending", "Restricted"].includes(
    accountConfigData?.merchantStatus ?? ""
  );

  const displayCapitalIcon =
    currentUserAccount?.type === "Customer" &&
    accountConfigData?.country === "US" &&
    accountConfigData.offerStatus === "enabled" &&
    !isPendingOrRestricted;

  const displayCapitalBanner =
    displayCapitalIcon && accountConfigData.grantStatus !== "enabled";

  useEffect(() => {
    const hideCapitalsBannerCookie =
      (cookies.hideCapitalsBanner as string) || "";
    setShowCapitalBanner(
      displayCapitalBanner &&
        hideCapitalsBannerCookie !== currentUserAccount?.accountHolderCode
    );
  }, []);

  function logVisit() {
    logPageVisit("Transactions", currentUserAccount?.username);
  }

  function handleDismissClick() {
    if (
      accountConfigData?.offerStatus === "enabled" &&
      accountConfigData.grantStatus !== "enabled"
    ) {
      setCookies("hideCapitalsBanner", currentUserAccount?.accountHolderCode);
    }
    setShowCapitalBanner(false);
  }

  return (
    <div className="flex flex-col items-center justify-center px-6">
      <Transition
        show={showCapitalBanner}
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        {showCapitalBanner && (
          <div className="flex h-44 w-[1000px] justify-between gap-y-4 overflow-hidden rounded-3xl border border-sky-200 bg-sky-50">
            <div className="flex flex-col justify-between p-6">
              <h3 className="text-xl font-semibold">
                Fast, Flexible Funding Available With A Click of a Button:{" "}
                <br /> Unlock funding to elevate your business!
              </h3>
              <div className="flex gap-x-4">
                <Button
                  size="sm"
                  className="bg-black hover:bg-gray-700"
                  onClick={() =>
                    navigate(
                      `/capital/${currentUserAccount?.accountHolderCode}`
                    )
                  }
                >
                  See offer
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="border-gray-300 text-black"
                  onClick={handleDismissClick}
                >
                  Dismiss
                </Button>
              </div>
            </div>
            <img
              src={banner}
              className="h-full w-5/12 object-cover"
              alt="Capital banner."
            />
          </div>
        )}
      </Transition>
      <div className="mt-32 flex w-full flex-wrap justify-around gap-x-10 gap-y-6 lg:w-1/3">
        <HomeIcon
          logPageVisit={logVisit}
          label="Transactions"
          to={getTransactionsPageDefaultUrl()}
          icon={<CurrencyDollarIcon className="w-24 text-primary-700" />}
        />
        <HomeIcon
          label="Reports"
          to="/reports"
          icon={<ClipboardDocumentListIcon className="w-24 text-primary-700" />}
        />
        <HasAccess allowedRoles={[...csrRoles]}>
          <HomeIcon
            label="Clients"
            to={getOnboardingPageDefaultUrl()}
            icon={<UserGroupIcon className="w-24 text-primary-700" />}
          />
        </HasAccess>
        <HomeIcon
          label={currentUserAccount?.type === "CSR" ? "System" : "Settings"}
          to={
            currentUserAccount?.type === "CSR"
              ? "/system-settings"
              : "/customer-settings"
          }
          icon={<Cog6ToothIcon className="w-24 text-primary-700" />}
        />
        <HomeIcon
          label="Payout"
          to={
            currentUserAccount?.type === "CSR"
              ? "/payout"
              : `/payout/${currentUserAccount?.accountHolderCode}`
          }
          icon={<BanknotesIcon className="w-24 text-primary-700" />}
        />
        {displayCapitalIcon && (
          <HomeIcon
            label="Capital Lending"
            to={`/capital/${currentUserAccount?.accountHolderCode}`}
            icon={<BuildingLibraryIcon className="w-24 text-primary-700" />}
          />
        )}
        <HasAccess allowedRoles={["ADMIN"]}>
          <HomeIcon
            label="System Tools"
            to={"/system-tools"}
            icon={<WrenchScrewdriverIcon className="w-24 text-primary-700" />}
          />
        </HasAccess>
      </div>
    </div>
  );
}

export default Home;
